<template>
  <div class="flex flex-col sm:px-6 lg:px-8">
    <div class="sm:mx-auto sm:w-full sm:max-w-[520px]">
      <div class="items-center bg-white px-6 md:px-12 pb-6 shadow sm:rounded-lg">
        <div class="flex flex-col items-center justify-center pt-14">
          <Icon class="text-5xl text-primary" name="encrypted" is-filled/>
          <SectionHeader
              class="mx-auto !justify-center pb-8"
              :title="copy"
          >
          </SectionHeader>

        </div>
        <div>
          <form @submit.prevent="handleSubmit">
            <div class="flex flex-col">
              <InputPassword
                  id="password"
                  v-model:value="values.password"
                  v-model:errors="errors.password"
                  :label="__('New Password')"
                  :placeholder="__('Add password')"
                  class="pb-5"
                  required
              />
              <div v-if="startTyping">
                <div class="flex flex-row gap-2 pb-3">
                  <div class="flex items-center gap-1">
                    <Icon
                        :class="[
                            passwordRules.min === null
                              ? 'text-gray-400'
                              : passwordRules.min
                                ? 'text-green-600'
                                : 'text-red-600'
                          ]"
                        :name="
                            passwordRules.min === null || passwordRules.min
                              ? 'done'
                              : 'close'
                          "
                    />
                    <span class="text-xs text-zinc-600">
                          {{ __('min 8 characters') }}
                        </span>
                  </div>
                  <div class="flex items-center gap-1">
                    <Icon
                        :class="[
                            passwordRules.min === null
                              ? 'text-gray-400'
                              : passwordRules.upper
                                ? 'text-green-600'
                                : 'text-red-600'
                          ]"
                        :name="
                            passwordRules.min === null || passwordRules.upper
                              ? 'done'
                              : 'close'
                          "
                    />
                    <span class="text-xs text-zinc-600">
                          {{ __('one uppercase letter') }}
                        </span>
                  </div>
                  <div class="flex items-center gap-1">
                    <Icon
                        :class="[
                            passwordRules.min === null
                              ? 'text-gray-400'
                              : passwordRules.lower
                                ? 'text-green-600'
                                : 'text-red-600'
                          ]"
                        :name="
                            passwordRules.min === null || passwordRules.lower
                              ? 'done'
                              : 'close'
                          "
                    />
                    <span class="text-xs text-zinc-600">
                          {{ __('one lowercase letter') }}
                        </span>
                  </div>
                </div>
                <div class="flex flex-row gap-2 pb-5">
                  <div class="flex items-center gap-1">
                    <Icon
                        :class="[
                            passwordRules.min === null
                              ? 'text-gray-400'
                              : passwordRules.number
                                ? 'text-green-600'
                                : 'text-red-600'
                          ]"
                        :name="
                            passwordRules.min === null || passwordRules.number
                              ? 'done'
                              : 'close'
                          "
                    />
                    <span class="text-xs text-zinc-600">
                          {{ __('one number') }}
                        </span>
                  </div>
                  <div class="flex items-center gap-1">
                    <Icon
                        :class="[
                            passwordRules.min === null
                              ? 'text-gray-400'
                              : passwordRules.special
                                ? 'text-green-600'
                                : 'text-red-600'
                          ]"
                        :name="
                            passwordRules.min === null || passwordRules.special
                              ? 'done'
                              : 'close'
                          "
                    />
                    <span class="text-xs text-zinc-600">
                          {{ __('one special character (@, #, $)') }}
                        </span>
                  </div>
                </div>
              </div>
              <InputPassword
                  id="password_confirmation"
                  v-model:value="values.password_confirmation"
                  v-model:errors="errors.password_confirmation"
                  :label="__('Confirm password')"
                  :placeholder="__('Add password')"
                  required
                  class="pb-8 md:col-start-2"
              />

              <template v-if="agreeWithTerms">
                <div class="space-y-2 pb-8">
                  <InputCheckbox
                      id="recruit"
                      v-model:value="values.recruit"
                      v-model:errors="errors.recruit"
                      :label="__('I agree to the')"
                      class="mb-6"
                  >
                    <template #default>
                      <a :href="props.terms" target="_blank" class="text-black underline">{{
                          __('terms and conditions')
                        }}</a>
                      {{ __(' and the ') }}
                      <a :href="props.privacy" target="_blank" class="text-black underline">{{
                          __('privacy policy')
                        }}</a
                      >.
                    </template>
                  </InputCheckbox>
                </div>
              </template>

              <div
                  v-if="errorMessage || successMessage"
                  class="mt-8 flex flex-col"
              >
                <div
                    v-if="errorMessage"
                    class="flex items-center gap-2 rounded-lg border border-red-400 bg-red-100 p-4 text-red-700"
                >
                  <span
                      class="material-symbols-outlined text-xl text-red-600"
                      style="font-variation-settings: 'FILL' 1"
                  >
                    error
                  </span>
                  <span>{{ errorMessage }}</span>
                </div>

                <div
                    v-if="successMessage"
                    class="flex items-center gap-2 rounded-lg border border-green-400 bg-green-100 p-4 text-green-700"
                >
                  <span
                      class="material-symbols-outlined text-xl text-green-600"
                      style="font-variation-settings: 'FILL' 1"
                  >
                    check_circle
                  </span>
                  <span>{{ successMessage }}</span>
                </div>
              </div>
            </div>

            <div
                class="mt-8 flex flex-col gap-2 md:flex-row-reverse md:justify-center items-center"
            >
              <Button
                  class="flex items-center justify-center gap-1.5"
                  :type="isLoading ? 'button' : 'submit'"
                  :disabled="isLoading"
                  style="width: 10rem !important"
              >
                <Icon
                    v-if="isLoading"
                    class="animate-spin text-lg"
                    name="progress_activity"
                />
                <span>{{ copy }}</span>
              </Button>
            </div>
          </form>
        </div>
        <div class="flex flex-col items-center justify-center">
          <a :href="loginUrl" class="text-md font-medium text-gray-700 underline mt-8 flex items-center gap-2">
            <img src="/images/front/arrow_back.png" alt="Back Arrow" class="w-5 h-5"/>
            {{ __('Back to login') }}
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {computed, ref, watch} from 'vue';
import {__} from '@/helpers';
import {Button} from '../../../../components/block/button';
import {Icon} from '../../../../components/block/icon';
import {SectionHeader} from '../../../components/section-header';
import axios from 'axios';
import {InputPassword} from '../../../../components/form/input-password';
import {InputCheckbox} from "../../../../components/form/input-checkbox";

const props = defineProps({
  token: String,
  email: String,
  copy: String,
  recruit: {
    type: Boolean,
    default: true,
    required: false,
  },
});

const values = ref({
  password: '',
  password_confirmation: '',
  token: props.token || '',
  email: props.email || '',
  recruit: props.recruit,
});

const errors = ref({
  password: [],
  password_confirmation: [],
  token: [],
  recruit: []
});

const isLoading = ref(false);
const startTyping = ref(false);

const passwordRules = ref({
  min: null,
  upper: null,
  lower: null,
  number: null,
  special: null
});

const agreeWithTerms = computed(() => {
  return props.recruit === false
});

watch(
    [() => values.value.password, () => values.value.password_confirmation],
    ([newPassword, confirmPassword]) => {
      if (newPassword.length > 0) {
        startTyping.value = true;
      }

      if (newPassword.length === 0) {
        startTyping.value = false;
      }

      passwordRules.value.min = newPassword.length === 0 ? null : newPassword.length >= 8;
      passwordRules.value.upper = newPassword.length === 0 ? null : /[A-Z]/.test(newPassword);
      passwordRules.value.lower = newPassword.length === 0 ? null : /[a-z]/.test(newPassword);
      passwordRules.value.number = newPassword.length === 0 ? null : /\d/.test(newPassword);
      passwordRules.value.special = newPassword.length === 0 ? null : /[^a-zA-Z0-9]/.test(newPassword);

      if (confirmPassword && newPassword !== confirmPassword) {
        errors.value.password_confirmation = [
          __('Your new password must match the confirmation password')
        ];
      } else {
        errors.value.password_confirmation = [];
      }
    }
);

const errorMessage = ref('');
const successMessage = ref('');
const loginUrl = '/login';

async function handleSubmit() {
  const requiredFieldKeys = ['password', 'password_confirmation', 'token'];
  let firstElement;

  requiredFieldKeys.forEach((key) => {
    if (!values.value[key]) {
      errors.value[key] = [__('This field is required')];

      const element = document.getElementById(key);

      if (element && !firstElement) {
        firstElement = element;
      }
    }
  });

  if (agreeWithTerms && !values.value.recruit) {
    errors.value.recruit = [__('You must agree to the terms and conditions.')];
  }

  if (Object.values(errors.value).some((error) => error.length)) {
    if (firstElement) {
      firstElement.scrollIntoView({behavior: 'smooth', block: 'center'});
    }
    return;
  }

  isLoading.value = true;
  errorMessage.value = '';

  try {
    const response = await axios.post(route('password.reset'), values.value);

    if (response.data.status === 'success') {
      window.location.href = response.data.redirect;
    }
  } catch (error) {
    if (error?.response?.data?.message) {
      errorMessage.value = error.response.data.message;
    } else {
      errorMessage.value = __(
          'An unexpected error has occurred. Please try again later.'
      );
    }
  }

  isLoading.value = false;
}
</script>
